/**
 * Test data for the real-voice test page (/voice-test).
 * phraseToSpeak = what TTS speaks; STT transcript is compared to these expectations.
 */

import hebrewTemplateVoiceTestLines from "./hebrewTemplateVoiceTestLines.json";

const hebrewProdSeenIds = new Set();
/** Unique templates from JSON (voice lines may repeat the same templateId for STT-style variants). */
export const MOCK_HEBREW_PRODUCTION_TEMPLATES = hebrewTemplateVoiceTestLines
  .filter((row) => {
    if (hebrewProdSeenIds.has(row.templateId)) return false;
    hebrewProdSeenIds.add(row.templateId);
    return true;
  })
  .map((row) => ({
    templateId: row.templateId,
    templateName: row.templateName,
    content: "",
  }));

/** One real-voice row per JSON entry (same order as hebrewTemplateVoiceTestLines.json). */
export const HEBREW_PRODUCTION_TEMPLATE_VOICE_TESTS = hebrewTemplateVoiceTestLines.map((row, idx) => ({
  id: `he-prod-tpl-${idx}`,
  lang: "he-IL",
  phraseToSpeak: row.voiceTestLine,
  expectMatch: true,
  expectedTemplateName: row.templateName,
}));

// ───────────────────────────────────────────────────────────────────────────
// Real English radiology templates.
// Inline (no separate JSON) — same row shape as hebrewTemplateVoiceTestLines.
// `voiceTestLine` is what TTS will speak in the trigger-based suite; the
// trigger-less suite strips the leading "Template " on the fly.
// ───────────────────────────────────────────────────────────────────────────
const englishTemplateVoiceTestLines = [
  { templateId: "en-tpl-chest-ct",        templateName: "Chest CT",
    voiceTestLine: "Template Chest CT multiplanar technique without contrast material clear lung fields" },
  { templateId: "en-tpl-abdomen-ct",      templateName: "Abdomen CT",
    voiceTestLine: "Template Abdomen CT with intravenous contrast no acute abnormality identified" },
  { templateId: "en-tpl-brain-mri",       templateName: "Brain MRI",
    voiceTestLine: "Template Brain MRI multiplanar imaging without contrast normal parenchyma" },
  { templateId: "en-tpl-cspine-mri",      templateName: "Cervical Spine MRI",
    voiceTestLine: "Template Cervical Spine MRI demonstrates normal alignment no significant herniation" },
  { templateId: "en-tpl-lspine-mri",      templateName: "Lumbar Spine MRI",
    voiceTestLine: "Template Lumbar Spine MRI shows mild multilevel degenerative changes" },
  { templateId: "en-tpl-knee-mri",        templateName: "Knee MRI",
    voiceTestLine: "Template Knee MRI menisci intact ligaments preserved no acute injury" },
  { templateId: "en-tpl-shoulder-mri",    templateName: "Shoulder MRI",
    voiceTestLine: "Template Shoulder MRI rotator cuff intact no labral tear" },
  { templateId: "en-tpl-chest-xray",      templateName: "Chest X-Ray",
    voiceTestLine: "Template Chest X-Ray clear lungs no acute cardiopulmonary process" },
  { templateId: "en-tpl-mammo-screening", templateName: "Mammography Screening",
    voiceTestLine: "Template Mammography Screening BI-RADS category one negative" },
  { templateId: "en-tpl-thyroid-us",      templateName: "Thyroid Ultrasound",
    voiceTestLine: "Template Thyroid Ultrasound homogeneous parenchyma no nodules" },
  { templateId: "en-tpl-cta-brain",       templateName: "CT Angiography Brain",
    voiceTestLine: "Template CT Angiography Brain no aneurysm or significant stenosis" },
  { templateId: "en-tpl-petct-whole",     templateName: "PET CT Whole Body",
    voiceTestLine: "Template PET CT Whole Body no hypermetabolic disease" },
  { templateId: "en-tpl-pelvic-ct",       templateName: "Pelvic CT",
    voiceTestLine: "Template Pelvic CT unremarkable findings no acute pelvic process" },
  { templateId: "en-tpl-abdomen-us",      templateName: "Abdominal Ultrasound",
    voiceTestLine: "Template Abdominal Ultrasound normal liver echotexture no biliary dilatation" },
];

export const MOCK_ENGLISH_PRODUCTION_TEMPLATES = englishTemplateVoiceTestLines.map((row) => ({
  templateId: row.templateId,
  templateName: row.templateName,
  content: "",
}));

export const ENGLISH_PRODUCTION_TEMPLATE_VOICE_TESTS = englishTemplateVoiceTestLines.map((row, idx) => ({
  id: `en-prod-tpl-${idx}`,
  lang: "en-US",
  phraseToSpeak: row.voiceTestLine,
  expectMatch: true,
  expectedTemplateName: row.templateName,
}));

// Combined real-templates list — what a multilingual radiologist would
// actually have loaded. Used by the trigger-less production suites so the
// engine has to disambiguate between Hebrew AND English candidates.
export const MOCK_PRODUCTION_TEMPLATES = [
  ...MOCK_HEBREW_PRODUCTION_TEMPLATES,
  ...MOCK_ENGLISH_PRODUCTION_TEMPLATES,
];

// Trigger-less variants of the real-name suites. Same expected template,
// but the speaker drops the "Template " / "תבנית " trigger word — the
// detector must still latch on via token-set matching.
//
// Hebrew + English rows live in a single array so the existing language
// dropdown filters them like every other multilingual suite.
const stripLeadingTriggerEn = (line) => line.replace(/^\s*template\s+/i, "");
const stripLeadingTriggerHe = (line) => line.replace(/^\s*תבנית\s+/, "");

export const TRIGGERLESS_PRODUCTION_TESTS = [
  ...hebrewTemplateVoiceTestLines.map((row, idx) => ({
    id: `he-prod-trigless-${idx}`,
    lang: "he-IL",
    phraseToSpeak: stripLeadingTriggerHe(row.voiceTestLine),
    expectMatch: true,
    expectedTemplateName: row.templateName,
  })),
  ...englishTemplateVoiceTestLines.map((row, idx) => ({
    id: `en-prod-trigless-${idx}`,
    lang: "en-US",
    phraseToSpeak: stripLeadingTriggerEn(row.voiceTestLine),
    expectMatch: true,
    expectedTemplateName: row.templateName,
  })),
];

// ----- Editor commands: phrase to speak + expected detection result -----
export const EDITOR_COMMAND_TESTS = [
  { id: "en-go-to", lang: "en-US", phraseToSpeak: "go to Findings", expected: { type: "navigation", action: "goTo", headerName: "Findings" } },
  {
    id: "en-go-to-caps",
    lang: "en-US",
    phraseToSpeak: "Go To Findings",
    expected: { type: "navigation", action: "goTo", headerName: "Findings" },
  },
  { id: "en-delete-word", lang: "en-US", phraseToSpeak: "delete word", expected: { type: "delete", action: "word" } },
  {
    id: "en-delete-word-caps",
    lang: "en-US",
    phraseToSpeak: "DELETE WORD",
    expected: { type: "delete", action: "word" },
  },
  { id: "en-delete-line", lang: "en-US", phraseToSpeak: "delete line", expected: { type: "delete", action: "line" } },
  { id: "en-delete-all", lang: "en-US", phraseToSpeak: "delete all", expected: { type: "delete", action: "all" } },
  { id: "en-undo", lang: "en-US", phraseToSpeak: "undo", expected: { type: "edit", action: "undo" } },
  { id: "en-undo-caps", lang: "en-US", phraseToSpeak: "Undo", expected: { type: "edit", action: "undo" } },
  { id: "en-redo", lang: "en-US", phraseToSpeak: "redo", expected: { type: "edit", action: "redo" } },
  { id: "en-uppercase", lang: "en-US", phraseToSpeak: "uppercase", expected: { type: "case", action: "uppercase" } },
  { id: "en-lowercase", lang: "en-US", phraseToSpeak: "lowercase", expected: { type: "case", action: "lowercase" } },
  { id: "en-bold", lang: "en-US", phraseToSpeak: "bold", expected: { type: "formatting", action: "bold" } },
  { id: "en-new-line", lang: "en-US", phraseToSpeak: "new line", expected: { type: "line", action: "newLine" } },
  { id: "en-paragraph", lang: "en-US", phraseToSpeak: "paragraph", expected: { type: "line", action: "paragraph" } },
  { id: "en-select-current-sentence", lang: "en-US", phraseToSpeak: "select current sentence", expected: { type: "selection", action: "currentSentence" } },
  { id: "en-select-current-paragraph", lang: "en-US", phraseToSpeak: "select current paragraph", expected: { type: "selection", action: "currentParagraph" } },
  { id: "en-select-next-sentence", lang: "en-US", phraseToSpeak: "select next sentence", expected: { type: "selection", action: "nextSentence" } },
  { id: "en-select-previous-sentence", lang: "en-US", phraseToSpeak: "select previous sentence", expected: { type: "selection", action: "previousSentence" } },
  { id: "en-select-next-paragraph", lang: "en-US", phraseToSpeak: "select next paragraph", expected: { type: "selection", action: "nextParagraph" } },
  { id: "en-select-current-field", lang: "en-US", phraseToSpeak: "select current field", expected: { type: "selection", action: "currentField" } },
  { id: "en-select-next-field", lang: "en-US", phraseToSpeak: "select next field", expected: { type: "selection", action: "nextField" } },
  { id: "en-select-previous-field", lang: "en-US", phraseToSpeak: "select previous field", expected: { type: "selection", action: "previousField" } },
  // Spanish – full set
  { id: "es-go-to", lang: "es-ES", phraseToSpeak: "ir a Conclusión", expected: { type: "navigation", action: "goTo", headerName: "Conclusión" } },
  { id: "es-delete-word", lang: "es-ES", phraseToSpeak: "borrar palabra", expected: { type: "delete", action: "word" } },
  { id: "es-delete-line", lang: "es-ES", phraseToSpeak: "borrar línea", expected: { type: "delete", action: "line" } },
  { id: "es-delete-all", lang: "es-ES", phraseToSpeak: "borrar todo", expected: { type: "delete", action: "all" } },
  { id: "es-undo", lang: "es-ES", phraseToSpeak: "deshacer", expected: { type: "edit", action: "undo" } },
  { id: "es-redo", lang: "es-ES", phraseToSpeak: "rehacer", expected: { type: "edit", action: "redo" } },
  { id: "es-uppercase", lang: "es-ES", phraseToSpeak: "mayúsculas", expected: { type: "case", action: "uppercase" } },
  { id: "es-lowercase", lang: "es-ES", phraseToSpeak: "minúsculas", expected: { type: "case", action: "lowercase" } },
  { id: "es-bold", lang: "es-ES", phraseToSpeak: "negrita", expected: { type: "formatting", action: "bold" } },
  { id: "es-new-line", lang: "es-ES", phraseToSpeak: "nueva línea", expected: { type: "line", action: "newLine" } },
  { id: "es-paragraph", lang: "es-ES", phraseToSpeak: "párrafo", expected: { type: "line", action: "paragraph" } },
  { id: "es-select-current-sentence", lang: "es-ES", phraseToSpeak: "seleccionar oración actual", expected: { type: "selection", action: "currentSentence" } },
  { id: "es-select-current-paragraph", lang: "es-ES", phraseToSpeak: "seleccionar párrafo actual", expected: { type: "selection", action: "currentParagraph" } },
  { id: "es-select-next-sentence", lang: "es-ES", phraseToSpeak: "seleccionar siguiente oración", expected: { type: "selection", action: "nextSentence" } },
  { id: "es-select-previous-sentence", lang: "es-ES", phraseToSpeak: "seleccionar oración anterior", expected: { type: "selection", action: "previousSentence" } },
  { id: "es-select-next-paragraph", lang: "es-ES", phraseToSpeak: "seleccionar siguiente párrafo", expected: { type: "selection", action: "nextParagraph" } },
  { id: "es-select-current-field", lang: "es-ES", phraseToSpeak: "seleccionar campo actual", expected: { type: "selection", action: "currentField" } },
  { id: "es-select-next-field", lang: "es-ES", phraseToSpeak: "seleccionar siguiente campo", expected: { type: "selection", action: "nextField" } },
  { id: "es-select-previous-field", lang: "es-ES", phraseToSpeak: "seleccionar campo anterior", expected: { type: "selection", action: "previousField" } },
  // French – full set
  { id: "fr-go-to", lang: "fr-FR", phraseToSpeak: "va à Conclusion", expected: { type: "navigation", action: "goTo", headerName: "Conclusion" } },
  { id: "fr-delete-word", lang: "fr-FR", phraseToSpeak: "supprimer mot", expected: { type: "delete", action: "word" } },
  { id: "fr-delete-line", lang: "fr-FR", phraseToSpeak: "supprimer ligne", expected: { type: "delete", action: "line" } },
  { id: "fr-delete-all", lang: "fr-FR", phraseToSpeak: "supprimer tout", expected: { type: "delete", action: "all" } },
  { id: "fr-undo", lang: "fr-FR", phraseToSpeak: "annuler", expected: { type: "edit", action: "undo" } },
  { id: "fr-redo", lang: "fr-FR", phraseToSpeak: "rétablir", expected: { type: "edit", action: "redo" } },
  { id: "fr-uppercase", lang: "fr-FR", phraseToSpeak: "majuscules", expected: { type: "case", action: "uppercase" } },
  { id: "fr-lowercase", lang: "fr-FR", phraseToSpeak: "minuscules", expected: { type: "case", action: "lowercase" } },
  { id: "fr-bold", lang: "fr-FR", phraseToSpeak: "gras", expected: { type: "formatting", action: "bold" } },
  { id: "fr-new-line", lang: "fr-FR", phraseToSpeak: "nouvelle ligne", expected: { type: "line", action: "newLine" } },
  { id: "fr-paragraph", lang: "fr-FR", phraseToSpeak: "paragraphe", expected: { type: "line", action: "paragraph" } },
  { id: "fr-select-current-sentence", lang: "fr-FR", phraseToSpeak: "sélectionner phrase actuelle", expected: { type: "selection", action: "currentSentence" } },
  { id: "fr-select-current-paragraph", lang: "fr-FR", phraseToSpeak: "sélectionner paragraphe actuel", expected: { type: "selection", action: "currentParagraph" } },
  { id: "fr-select-next-sentence", lang: "fr-FR", phraseToSpeak: "sélectionner phrase suivante", expected: { type: "selection", action: "nextSentence" } },
  { id: "fr-select-previous-sentence", lang: "fr-FR", phraseToSpeak: "sélectionner phrase précédente", expected: { type: "selection", action: "previousSentence" } },
  { id: "fr-select-next-paragraph", lang: "fr-FR", phraseToSpeak: "sélectionner paragraphe suivant", expected: { type: "selection", action: "nextParagraph" } },
  { id: "fr-select-current-field", lang: "fr-FR", phraseToSpeak: "sélectionner champ actuel", expected: { type: "selection", action: "currentField" } },
  { id: "fr-select-next-field", lang: "fr-FR", phraseToSpeak: "sélectionner champ suivant", expected: { type: "selection", action: "nextField" } },
  { id: "fr-select-previous-field", lang: "fr-FR", phraseToSpeak: "sélectionner champ précédent", expected: { type: "selection", action: "previousField" } },
  // Hebrew – full set
  { id: "he-go-to", lang: "he-IL", phraseToSpeak: "לך לממצאים", expected: { type: "navigation", action: "goTo", headerName: "ממצאים" } },
  { id: "he-delete-word", lang: "he-IL", phraseToSpeak: "מחק מילה", expected: { type: "delete", action: "word" } },
  { id: "he-delete-line", lang: "he-IL", phraseToSpeak: "מחק שורה", expected: { type: "delete", action: "line" } },
  { id: "he-delete-all", lang: "he-IL", phraseToSpeak: "מחק הכל", expected: { type: "delete", action: "all" } },
  { id: "he-undo", lang: "he-IL", phraseToSpeak: "בטל", expected: { type: "edit", action: "undo" } },
  { id: "he-redo", lang: "he-IL", phraseToSpeak: "חזור", expected: { type: "edit", action: "redo" } },
  { id: "he-uppercase", lang: "he-IL", phraseToSpeak: "אותיות גדולות", expected: { type: "case", action: "uppercase" } },
  { id: "he-lowercase", lang: "he-IL", phraseToSpeak: "אותיות קטנות", expected: { type: "case", action: "lowercase" } },
  { id: "he-bold", lang: "he-IL", phraseToSpeak: "מודגש", expected: { type: "formatting", action: "bold" } },
  { id: "he-new-line", lang: "he-IL", phraseToSpeak: "שורה חדשה", expected: { type: "line", action: "newLine" } },
  { id: "he-paragraph", lang: "he-IL", phraseToSpeak: "פסקה", expected: { type: "line", action: "paragraph" } },
  { id: "he-select-current-sentence", lang: "he-IL", phraseToSpeak: "בחר משפט נוכחי", expected: { type: "selection", action: "currentSentence" } },
  { id: "he-select-current-paragraph", lang: "he-IL", phraseToSpeak: "בחר פסקה נוכחית", expected: { type: "selection", action: "currentParagraph" } },
  { id: "he-select-next-sentence", lang: "he-IL", phraseToSpeak: "בחר משפט הבא", expected: { type: "selection", action: "nextSentence" } },
  { id: "he-select-previous-sentence", lang: "he-IL", phraseToSpeak: "בחר משפט קודם", expected: { type: "selection", action: "previousSentence" } },
  { id: "he-select-next-paragraph", lang: "he-IL", phraseToSpeak: "בחר פסקה הבאה", expected: { type: "selection", action: "nextParagraph" } },
  { id: "he-select-current-field", lang: "he-IL", phraseToSpeak: "בחר שדה נוכחי", expected: { type: "selection", action: "currentField" } },
  { id: "he-select-next-field", lang: "he-IL", phraseToSpeak: "בחר שדה הבא", expected: { type: "selection", action: "nextField" } },
  { id: "he-select-previous-field", lang: "he-IL", phraseToSpeak: "בחר שדה קודם", expected: { type: "selection", action: "previousField" } },
  // Arabic – full set
  { id: "ar-go-to", lang: "ar-SA", phraseToSpeak: "اذهب إلى الاستنتاج", expected: { type: "navigation", action: "goTo", headerName: "الاستنتاج" } },
  { id: "ar-delete-word", lang: "ar-SA", phraseToSpeak: "احذف كلمة", expected: { type: "delete", action: "word" } },
  { id: "ar-delete-line", lang: "ar-SA", phraseToSpeak: "احذف سطر", expected: { type: "delete", action: "line" } },
  { id: "ar-delete-all", lang: "ar-SA", phraseToSpeak: "احذف الكل", expected: { type: "delete", action: "all" } },
  { id: "ar-undo", lang: "ar-SA", phraseToSpeak: "تراجع", expected: { type: "edit", action: "undo" } },
  { id: "ar-redo", lang: "ar-SA", phraseToSpeak: "كرر", expected: { type: "edit", action: "redo" } },
  { id: "ar-uppercase", lang: "ar-SA", phraseToSpeak: "أحرف كبيرة", expected: { type: "case", action: "uppercase" } },
  { id: "ar-lowercase", lang: "ar-SA", phraseToSpeak: "أحرف صغيرة", expected: { type: "case", action: "lowercase" } },
  { id: "ar-bold", lang: "ar-SA", phraseToSpeak: "عريض", expected: { type: "formatting", action: "bold" } },
  { id: "ar-new-line", lang: "ar-SA", phraseToSpeak: "سطر جديد", expected: { type: "line", action: "newLine" } },
  { id: "ar-paragraph", lang: "ar-SA", phraseToSpeak: "فقرة", expected: { type: "line", action: "paragraph" } },
  { id: "ar-select-current-sentence", lang: "ar-SA", phraseToSpeak: "اختر الجملة الحالية", expected: { type: "selection", action: "currentSentence" } },
  { id: "ar-select-current-paragraph", lang: "ar-SA", phraseToSpeak: "اختر الفقرة الحالية", expected: { type: "selection", action: "currentParagraph" } },
  { id: "ar-select-next-sentence", lang: "ar-SA", phraseToSpeak: "اختر الجملة التالية", expected: { type: "selection", action: "nextSentence" } },
  { id: "ar-select-previous-sentence", lang: "ar-SA", phraseToSpeak: "اختر الجملة السابقة", expected: { type: "selection", action: "previousSentence" } },
  { id: "ar-select-next-paragraph", lang: "ar-SA", phraseToSpeak: "اختر الفقرة التالية", expected: { type: "selection", action: "nextParagraph" } },
  { id: "ar-select-current-field", lang: "ar-SA", phraseToSpeak: "اختر الحقل الحالي", expected: { type: "selection", action: "currentField" } },
  { id: "ar-select-next-field", lang: "ar-SA", phraseToSpeak: "اختر الحقل التالي", expected: { type: "selection", action: "nextField" } },
  { id: "ar-select-previous-field", lang: "ar-SA", phraseToSpeak: "اختر الحقل السابق", expected: { type: "selection", action: "previousField" } },
];

export const MOCK_MACROS = [
  { macroName: "Fatty Liver", content: "<p>Fatty liver.</p>" },
  { macroName: "Normal", content: "<p>Normal.</p>" },
  { macroName: "No Findings", content: "<p>No significant findings.</p>" },
  { macroName: "Unremarkable", content: "<p>Unremarkable.</p>" },
  { macroName: "Within Normal Limits", content: "<p>Within normal limits.</p>" },
  { macroName: "Conclusion", content: "<p>Conclusion section.</p>" },
];

export const MACRO_TESTS = [
  { id: "macro-en-short-fatty", lang: "en-US", phraseToSpeak: "Short fatty liver", expectMatch: true, expectedMacroName: "Fatty Liver" },
  { id: "macro-en-insert-fatty", lang: "en-US", phraseToSpeak: "Insert fatty liver", expectMatch: true, expectedMacroName: "Fatty Liver" },
  { id: "macro-en-short-normal", lang: "en-US", phraseToSpeak: "Short Normal", expectMatch: true, expectedMacroName: "Normal" },
  { id: "macro-en-short-no-findings", lang: "en-US", phraseToSpeak: "Short no findings", expectMatch: true, expectedMacroName: "No Findings" },
  { id: "macro-en-short-unremarkable", lang: "en-US", phraseToSpeak: "Short unremarkable", expectMatch: true, expectedMacroName: "Unremarkable" },
  { id: "macro-en-short-wnl", lang: "en-US", phraseToSpeak: "Short within normal limits", expectMatch: true, expectedMacroName: "Within Normal Limits" },
  { id: "macro-en-short-conclusion", lang: "en-US", phraseToSpeak: "Short conclusion", expectMatch: true, expectedMacroName: "Conclusion" },
  { id: "macro-en-unknown", lang: "en-US", phraseToSpeak: "Short unknown macro", expectMatch: false, expectNoMacro: true },
  { id: "macro-es-short-normal", lang: "es-ES", phraseToSpeak: "Short Normal", expectMatch: true, expectedMacroName: "Normal" },
  { id: "macro-es-short-fatty", lang: "es-ES", phraseToSpeak: "Short fatty liver", expectMatch: true, expectedMacroName: "Fatty Liver" },
  { id: "macro-es-short-no-findings", lang: "es-ES", phraseToSpeak: "Short no findings", expectMatch: true, expectedMacroName: "No Findings" },
  { id: "macro-es-unknown", lang: "es-ES", phraseToSpeak: "Short algo desconocido", expectMatch: false, expectNoMacro: true },
  { id: "macro-fr-raccourci-fatty", lang: "fr-FR", phraseToSpeak: "Raccourci fatty liver", expectMatch: true, expectedMacroName: "Fatty Liver" },
  { id: "macro-fr-raccourci-normal", lang: "fr-FR", phraseToSpeak: "Raccourci Normal", expectMatch: true, expectedMacroName: "Normal" },
  { id: "macro-fr-macro-normal", lang: "fr-FR", phraseToSpeak: "macro Normal", expectMatch: true, expectedMacroName: "Normal" },
  { id: "macro-fr-macro-conclusion", lang: "fr-FR", phraseToSpeak: "macro conclusion", expectMatch: true, expectedMacroName: "Conclusion" },
  { id: "macro-fr-raccourci-unremarkable", lang: "fr-FR", phraseToSpeak: "Raccourci unremarkable", expectMatch: true, expectedMacroName: "Unremarkable" },
  { id: "macro-fr-unknown", lang: "fr-FR", phraseToSpeak: "Raccourci inconnu", expectMatch: false, expectNoMacro: true },
  { id: "macro-he-makro-normal", lang: "he-IL", phraseToSpeak: "מאקרו Normal", expectMatch: true, expectedMacroName: "Normal" },
  { id: "macro-he-kitzur-normal", lang: "he-IL", phraseToSpeak: "קיצור Normal", expectMatch: true, expectedMacroName: "Normal" },
  { id: "macro-he-kitzur-fatty", lang: "he-IL", phraseToSpeak: "קיצור Fatty Liver", expectMatch: true, expectedMacroName: "Fatty Liver" },
  { id: "macro-he-kitzur-conclusion", lang: "he-IL", phraseToSpeak: "קיצור Conclusion", expectMatch: true, expectedMacroName: "Conclusion" },
  { id: "macro-he-unknown", lang: "he-IL", phraseToSpeak: "קיצור unknown", expectMatch: false, expectNoMacro: true },
  { id: "macro-ar-short-normal", lang: "ar-SA", phraseToSpeak: "Short Normal", expectMatch: true, expectedMacroName: "Normal" },
  { id: "macro-ar-short-fatty", lang: "ar-SA", phraseToSpeak: "Short fatty liver", expectMatch: true, expectedMacroName: "Fatty Liver" },
  { id: "macro-ar-short-conclusion", lang: "ar-SA", phraseToSpeak: "Short conclusion", expectMatch: true, expectedMacroName: "Conclusion" },
  { id: "macro-ar-unknown", lang: "ar-SA", phraseToSpeak: "Short unknown", expectMatch: false, expectNoMacro: true },
];

export const MOCK_TEMPLATES = [
  { templateName: "Chest CT", content: "<p>Chest CT template.</p>" },
  { templateName: "Abdomen", content: "<p>Abdomen template.</p>" },
  { templateName: "Brain MRI", content: "<p>Brain MRI template.</p>" },
  { templateName: "Liver Protocol", content: "<p>Liver protocol template.</p>" },
  { templateName: "Conclusion", content: "<p>Conclusion template.</p>" },
  { templateName: "Findings", content: "<p>Findings template.</p>" },
  { templateName: "Spine", content: "<p>Spine template.</p>" },
  { templateName: "Cardiac", content: "<p>Cardiac template.</p>" },
  { templateName: "US Abdomen", content: "<p>US Abdomen template.</p>" },
  { templateName: "Ultrasound Kidney", content: "<p>Ultrasound Kidney template.</p>" },
];

export const TEMPLATE_TESTS = [
  { id: "tpl-en-chest", lang: "en-US", phraseToSpeak: "Template Chest CT", expectMatch: true, expectedTemplateName: "Chest CT" },
  { id: "tpl-en-abdomen", lang: "en-US", phraseToSpeak: "template abdomen", expectMatch: true, expectedTemplateName: "Abdomen" },
  { id: "tpl-en-brain", lang: "en-US", phraseToSpeak: "Template Brain MRI", expectMatch: true, expectedTemplateName: "Brain MRI" },
  { id: "tpl-en-liver", lang: "en-US", phraseToSpeak: "template liver protocol", expectMatch: true, expectedTemplateName: "Liver Protocol" },
  { id: "tpl-en-conclusion", lang: "en-US", phraseToSpeak: "Template conclusion", expectMatch: true, expectedTemplateName: "Conclusion" },
  { id: "tpl-en-findings", lang: "en-US", phraseToSpeak: "template findings", expectMatch: true, expectedTemplateName: "Findings" },
  { id: "tpl-en-spine", lang: "en-US", phraseToSpeak: "Template spine", expectMatch: true, expectedTemplateName: "Spine" },
  { id: "tpl-en-cardiac", lang: "en-US", phraseToSpeak: "template cardiac", expectMatch: true, expectedTemplateName: "Cardiac" },
  // Ultrasound modality aliases: ultrasound / US / "you ess"
  { id: "tpl-en-us-abdomen-ultrasound", lang: "en-US", phraseToSpeak: "template ultrasound abdomen", expectMatch: true, expectedTemplateName: "US Abdomen" },
  { id: "tpl-en-us-abdomen-you-ess", lang: "en-US", phraseToSpeak: "template you ess abdomen", expectMatch: true, expectedTemplateName: "US Abdomen" },
  { id: "tpl-en-us-abdomen-us", lang: "en-US", phraseToSpeak: "template US abdomen", expectMatch: true, expectedTemplateName: "US Abdomen" },
  { id: "tpl-en-ultrasound-kidney-us", lang: "en-US", phraseToSpeak: "template US kidney", expectMatch: true, expectedTemplateName: "Ultrasound Kidney" },
  { id: "tpl-en-unknown", lang: "en-US", phraseToSpeak: "Template unknown", expectMatch: false, expectNoTemplate: true },
  { id: "tpl-es-chest", lang: "es-ES", phraseToSpeak: "Template Chest CT", expectMatch: true, expectedTemplateName: "Chest CT" },
  { id: "tpl-es-abdomen", lang: "es-ES", phraseToSpeak: "template Abdomen", expectMatch: true, expectedTemplateName: "Abdomen" },
  { id: "tpl-es-brain", lang: "es-ES", phraseToSpeak: "Template Brain MRI", expectMatch: true, expectedTemplateName: "Brain MRI" },
  { id: "tpl-es-conclusion", lang: "es-ES", phraseToSpeak: "template conclusion", expectMatch: true, expectedTemplateName: "Conclusion" },
  { id: "tpl-es-unknown", lang: "es-ES", phraseToSpeak: "Template desconocido", expectMatch: false, expectNoTemplate: true },
  { id: "tpl-fr-modele-chest", lang: "fr-FR", phraseToSpeak: "Modèle Chest CT", expectMatch: true, expectedTemplateName: "Chest CT" },
  { id: "tpl-fr-modele-abdomen", lang: "fr-FR", phraseToSpeak: "modèle Abdomen", expectMatch: true, expectedTemplateName: "Abdomen" },
  { id: "tpl-fr-modele-brain", lang: "fr-FR", phraseToSpeak: "Modèle Brain MRI", expectMatch: true, expectedTemplateName: "Brain MRI" },
  { id: "tpl-fr-template-liver", lang: "fr-FR", phraseToSpeak: "template liver protocol", expectMatch: true, expectedTemplateName: "Liver Protocol" },
  { id: "tpl-fr-modele-conclusion", lang: "fr-FR", phraseToSpeak: "modèle Conclusion", expectMatch: true, expectedTemplateName: "Conclusion" },
  { id: "tpl-fr-modele-findings", lang: "fr-FR", phraseToSpeak: "Modèle findings", expectMatch: true, expectedTemplateName: "Findings" },
  { id: "tpl-fr-unknown", lang: "fr-FR", phraseToSpeak: "Modèle inconnu", expectMatch: false, expectNoTemplate: true },
  { id: "tpl-he-tabnit-chest", lang: "he-IL", phraseToSpeak: "תבנית Chest CT", expectMatch: true, expectedTemplateName: "Chest CT" },
  { id: "tpl-he-tabnit-abdomen", lang: "he-IL", phraseToSpeak: "תבנית Abdomen", expectMatch: true, expectedTemplateName: "Abdomen" },
  { id: "tpl-he-tabnit-brain", lang: "he-IL", phraseToSpeak: "תבנית Brain MRI", expectMatch: true, expectedTemplateName: "Brain MRI" },
  { id: "tpl-he-tabnit-conclusion", lang: "he-IL", phraseToSpeak: "תבנית Conclusion", expectMatch: true, expectedTemplateName: "Conclusion" },
  { id: "tpl-he-unknown", lang: "he-IL", phraseToSpeak: "תבנית unknown", expectMatch: false, expectNoTemplate: true },
  { id: "tpl-ar-chest", lang: "ar-SA", phraseToSpeak: "Template Chest CT", expectMatch: true, expectedTemplateName: "Chest CT" },
  { id: "tpl-ar-abdomen", lang: "ar-SA", phraseToSpeak: "template Abdomen", expectMatch: true, expectedTemplateName: "Abdomen" },
  { id: "tpl-ar-brain", lang: "ar-SA", phraseToSpeak: "Template Brain MRI", expectMatch: true, expectedTemplateName: "Brain MRI" },
  { id: "tpl-ar-conclusion", lang: "ar-SA", phraseToSpeak: "template conclusion", expectMatch: true, expectedTemplateName: "Conclusion" },
  { id: "tpl-ar-unknown", lang: "ar-SA", phraseToSpeak: "Template unknown", expectMatch: false, expectNoTemplate: true },
];

// Trigger-less template auto-match fixtures.
//
// These are plain dictation prefixes (no "תבנית" / "template" / "modèle"
// trigger word). The detector must still identify the correct template from
// the first 5–8 words using literal token-set matching, fuzzy substring
// tolerance, and filler-word stripping. Broad modality aliases are still
// intentionally NOT applied (so "MR" will NOT match "MRI", "סי טי" will NOT
// match "CT"). Ultrasound is the exception: "ultrasound" / "US" / "you ess"
// are equivalent. Spec scenarios that depend on other aliases are listed
// below as `expectNoTemplate` to document the current product decision.
//
// `phraseToSpeak` is intentionally longer than just the template name — the
// suffix simulates real-world dictation where the user continues straight
// into clinical findings. Detection must still latch on the leading window
// without dragging the findings into the template match.
export const TRIGGERLESS_TEMPLATE_TESTS = [
  // Literal modality + anatomy combos (no aliases needed)
  {
    id: "trigless-en-chest-ct-plain",
    lang: "en-US",
    phraseToSpeak: "Chest CT no acute findings clear lungs",
    expectMatch: true,
    expectedTemplateName: "Chest CT",
  },
  {
    id: "trigless-en-brain-mri-literal",
    lang: "en-US",
    phraseToSpeak: "Brain MRI study shows normal parenchyma",
    expectMatch: true,
    expectedTemplateName: "Brain MRI",
  },
  // Word-order independence (English)
  {
    id: "trigless-en-ct-chest-reordered",
    lang: "en-US",
    phraseToSpeak: "CT of the chest small nodule right upper lobe",
    expectMatch: true,
    expectedTemplateName: "Chest CT",
  },
  // Hebrew: "בדיקת" is normal dictation wording (not a template trigger); title still matches
  {
    id: "trigless-he-bdikat-allowed",
    lang: "he-IL",
    phraseToSpeak: "בדיקת CT בטן ללא ועם הזרקת חומר ניגוד כבד שומני",
    expectMatch: true,
    expectedTemplateName: "CT בטן",
  },
  // Hebrew: filler words then template title (sidebar-style; no בדיקת trigger)
  {
    id: "trigless-he-want-exam-prefix",
    lang: "he-IL",
    phraseToSpeak: "רוצה בדיקת CT בטן ללא ועם הזרקת חומר ניגוד",
    expectMatch: true,
    expectedTemplateName: "CT בטן",
  },
  // Reverse word order: anatomy first, modality second
  {
    id: "trigless-he-anatomy-first",
    lang: "he-IL",
    phraseToSpeak: "בטן CT עם וללא חומר ניגוד",
    expectMatch: true,
    expectedTemplateName: "CT בטן",
  },
  // Hebrew transliteration "סי טי" — intentionally NOT matched (no alias map)
  {
    id: "trigless-he-ct-transliteration-no-alias",
    lang: "he-IL",
    phraseToSpeak: "סי טי בטן ללא ועם חומר ניגוד אבן בכליה שמאלית",
    expectMatch: false,
    expectNoTemplate: true,
  },
  // English "MR" abbreviation — intentionally NOT matched (no alias map)
  {
    id: "trigless-en-mr-abbrev-no-alias",
    lang: "en-US",
    phraseToSpeak: "Brain MR study shows normal parenchyma",
    expectMatch: false,
    expectNoTemplate: true,
  },
  // Precision-first: short irrelevant prefix must NOT match anything
  {
    id: "trigless-en-no-match-short",
    lang: "en-US",
    phraseToSpeak: "Patient presents with mild fatigue",
    expectMatch: false,
    expectNoTemplate: true,
  },
  // Precision-first: a single recognisable modality token alone must not
  // win — coverage gate requires ≥2 matched template tokens for >2-token
  // template names.
  {
    id: "trigless-en-no-match-single-token",
    lang: "en-US",
    phraseToSpeak: "CT showed no significant abnormality elsewhere",
    expectMatch: false,
    expectNoTemplate: true,
  },
];
